/**
 * The numbers behind the Home sections, worked out once for the web dashboard
 * and the native app: pipeline counts per stage group, certified and paid per
 * project, the six-month profit and loss, and Coming up due labels.
 */
import type { InterimApplication, Project } from '../../types/database';
import { PIPELINE_BUCKETS, stageInGroup, type StageGroupKey } from '../../features/dashboard/stages';
import { deriveProjectCash, safeParse } from '../../features/dashboard/utils';
import { calendarDaysBetween, type ActionItem } from '../../features/dashboard/actionQueueItems';
import { getDaysUntil } from '../../utils/dateFormat';
import type { SharedPalette } from '../theme';

// ── Pipeline ────────────────────────────────────────────────────────────────

/** The headline funnel shown on every width; the rest need room for all seven. */
export const KEY_PIPELINE_STAGES: readonly StageGroupKey[] = ['tendering', 'submitted', 'onSite'];

/** Bar colour per stage group, as a palette key or a fixed colour. */
const PIPELINE_TONES: Record<StageGroupKey, keyof SharedPalette | `#${string}`> = {
  tendering: 'warning',
  submitted: 'info',
  preStart: '#8b5cf6',
  onSite: 'success',
  finalAccount: 'brand',
  retention: '#06b6d4',
  onHold: 'muted',
};

export function pipelineColour(key: StageGroupKey, palette: SharedPalette): string {
  const tone = PIPELINE_TONES[key];
  return tone.startsWith('#') ? tone : palette[tone as keyof SharedPalette];
}

export interface PipelineRow {
  key: StageGroupKey;
  label: string;
  count: number;
  isKey: boolean;
}

export function buildPipelineRows(projects: Project[]): PipelineRow[] {
  const active = projects.filter((p) => p.archiveStatus !== 'archived');
  return PIPELINE_BUCKETS.map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
    count: active.filter((p) => stageInGroup(p.stage as string, bucket.key)).length,
    isKey: KEY_PIPELINE_STAGES.includes(bucket.key),
  }));
}

/**
 * Bar height as a percentage. A square-root scale keeps small stages readable
 * beside one dominant stage; a floor keeps any non-zero bar visible.
 */
export function pipelineBarPercent(count: number, rows: PipelineRow[]): number {
  if (count === 0) return 0;
  const sqrtMax = Math.sqrt(Math.max(...rows.map((r) => r.count), 1));
  return Math.max(8, (Math.sqrt(count) / sqrtMax) * 100);
}

// ── Outstanding ─────────────────────────────────────────────────────────────

export interface OutstandingRow {
  project: Project;
  certified: number;
  paid: number;
  outstanding: number;
  /** Days to the next unpaid application's due date; negative when overdue. */
  nextDays: number | null;
}

/** The projects with certified work, most outstanding first. */
export function buildOutstandingRows(projects: Project[], applications: InterimApplication[], maxRows = 6): OutstandingRow[] {
  return projects
    .map((project) => {
      const projectApps = applications.filter((a) => a.projectId === project.id);
      const { certified, paid, outstanding } = deriveProjectCash(project, projectApps);
      const nextDue = projectApps
        .filter((a) => a.status !== 'paid' && a.status !== 'rejected')
        .map((a) => safeParse(a.dueDateFinalPayment ?? a.dueDatePaymentNotice ?? a.periodEnd))
        .filter((d): d is Date => !!d)
        .sort((a, b) => a.getTime() - b.getTime())[0] ?? null;
      return { project, certified, paid, outstanding, nextDays: nextDue ? getDaysUntil(nextDue) : null };
    })
    .filter((r) => r.certified > 0)
    .sort((a, b) => b.outstanding - a.outstanding)
    .slice(0, maxRows);
}

export type DueTone = 'danger' | 'warning' | 'muted';

/** "3d overdue", "due today" or "5d", with its colour. */
export function outstandingDueCue(nextDays: number): { label: string; tone: DueTone } {
  if (nextDays < 0) return { label: `${Math.abs(nextDays)}d overdue`, tone: 'danger' };
  if (nextDays === 0) return { label: 'due today', tone: 'warning' };
  return { label: `${nextDays}d`, tone: nextDays <= 7 ? 'warning' : 'muted' };
}

// ── Profit and loss ─────────────────────────────────────────────────────────

export interface ProfitLossMonth {
  monthKey: string;
  label: string;
  income: number;
  expenses: number;
  net: number;
}

export interface ProfitLossSummary {
  hasActivity: boolean;
  periodNet: number;
  periodIncome: number;
  periodExpenses: number;
  /** Where the zero baseline sits, as a percentage from the top. */
  zeroTopPercent: number;
  /** Each month's bar: height and top offset as percentages, and direction. */
  bars: Array<{ monthKey: string; label: string; heightPercent: number; topPercent: number; up: boolean }>;
}

export function summariseProfitLoss(months: ProfitLossMonth[]): ProfitLossSummary {
  const hasActivity = months.some((m) => m.income !== 0 || m.expenses !== 0);
  const posMax = Math.max(0, ...months.map((m) => m.net));
  const negMin = Math.min(0, ...months.map((m) => m.net));
  const span = posMax - negMin || 1;
  // With no activity the baseline sits mid-height so the card still reads as a chart.
  const zeroTopPercent = hasActivity ? (posMax / span) * 100 : 50;
  return {
    hasActivity,
    periodNet: months.reduce((t, m) => t + m.net, 0),
    periodIncome: months.reduce((t, m) => t + m.income, 0),
    periodExpenses: months.reduce((t, m) => t + m.expenses, 0),
    zeroTopPercent,
    bars: months.map((m) => {
      const pct = (Math.abs(m.net) / span) * 100;
      const up = m.net >= 0;
      return {
        monthKey: m.monthKey,
        label: m.label,
        heightPercent: Math.max(m.net === 0 ? 0 : 4, pct),
        topPercent: up ? zeroTopPercent - pct : zeroTopPercent,
        up,
      };
    }),
  };
}

// ── Coming up ───────────────────────────────────────────────────────────────

/** "2d over", "today" or "3d", with its colour. */
export function comingUpDueCue(item: Pick<ActionItem, 'due' | 'overdue'>, today: Date): { label: string; tone: DueTone } {
  const days = item.due ? calendarDaysBetween(today, item.due) : null;
  const label = days == null ? '-' : days < 0 ? `${Math.abs(days)}d over` : days === 0 ? 'today' : `${days}d`;
  const tone: DueTone = item.overdue ? 'danger' : days != null && days <= 1 ? 'warning' : 'muted';
  return { label, tone };
}

/** Overdue items get their own heading rather than silently leading the list. */
export function comingUpSections<T extends Pick<ActionItem, 'overdue'>>(items: T[]) {
  return [
    { key: 'overdue' as const, label: 'Overdue', rows: items.filter((it) => it.overdue) },
    { key: 'upcoming' as const, label: 'Upcoming', rows: items.filter((it) => !it.overdue) },
  ].filter((s) => s.rows.length > 0);
}
